import React from "react";
import styled from "styled-components";
import { useCartStore, useStore } from "../../store/useStore";
import { useNavigate } from "react-router-dom";
import { FaTrash } from "react-icons/fa";

const Wrapper = styled.section`
  max-width: 500px;
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.07);
  padding: 2.5rem 1.5rem 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  font-family: "Playfair Display", serif;
  font-size: 2rem;
  color: #e97d7dff;
  margin-bottom: 1.2rem;
  text-align: center;
`;

const CartList = styled.ul`
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
`;

const CartItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f0eaea;
  font-size: 1.08rem;
`;

const CartItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CartItemActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 70px;
  gap: 1.2rem;
  margin-top: 0.2rem;
`;

const ItemImage = styled.img`
  width: 54px;
  height: 54px;
  object-fit: cover;
  border-radius: 0.7rem;
  margin-right: 1.1rem;
  background: #f7f7f7;
  box-shadow: 0 1px 6px rgba(233, 125, 125, 0.07);
`;

const ItemName = styled.span`
  flex: 2;
  color: #222;
  font-size: 1.08rem;
`;

const QtyControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const QtyBtn = styled.button`
  background: #f7f7f7;
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  font-size: 1.1rem;
  color: #e97d7dff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #e97d7dff;
    color: #fff;
  }
`;

const ItemQty = styled.span`
  min-width: 28px;
  text-align: center;
  color: #888;
  font-size: 1.08rem;
`;

const ItemPrice = styled.span`
  flex: 0 0 90px;
  text-align: right;
  color: #e97d7dff;
  font-weight: 600;
  font-size: 1.08rem;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #e97d7dff;
  font-size: 1.2rem;
  cursor: pointer;
  margin-left: 0.7rem;
  &:hover {
    color: #000;
  }
`;

const Total = styled.div`
  width: 100%;
  text-align: right;
  font-size: 1.15rem;
  font-weight: 600;
  color: #222;
  margin-bottom: 1.5rem;
`;

const Empty = styled.div`
  color: #888;
  font-size: 1.1rem;
  margin: 2rem 0;
  text-align: center;
`;

const CheckoutBtn = styled.button`
  background: #e97d7dff;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #fff;
    color: #e97d7dff;
    border: 2px solid #e97d7dff;
  }
`;

const Cart: React.FC = () => {
  const items = useCartStore((state) => state.items);
  const addToCart = useCartStore((state) => state.addToCart);
  const removeFromCart = useCartStore((state) => state.removeFromCart);
  const setCartOpen = useStore((state) => state.setCartOpen);
  const total = items.reduce(
    (sum: number, i: { price: number; qty: number }) => sum + i.price * i.qty,
    0
  );
  const navigate = useNavigate();

  // Helper to decrement quantity or remove if qty=1
  const handleDecrement = (item: any) => {
    if (item.qty > 1) {
      addToCart({ ...item, qty: -1 });
    } else {
      removeFromCart(item.id);
    }
  };

  const handleCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <Wrapper>
      <Title>Your Cart</Title>
      {items.length === 0 ? (
        <Empty>Your cart is empty.</Empty>
      ) : (
        <>
          <CartList>
            {items.map((item) => (
              <CartItem key={item.id}>
                <CartItemRow>
                  {item.images && item.images.length > 0 && (
                    <ItemImage src={item.images[0]} alt={item.name} />
                  )}
                  <ItemName>{item.name}</ItemName>
                  <ItemPrice>${(item.price * item.qty).toFixed(2)}</ItemPrice>
                </CartItemRow>
                <CartItemActions>
                  <QtyControls>
                    <QtyBtn
                      onClick={() => handleDecrement(item)}
                      title="Decrease"
                    >
                      -
                    </QtyBtn>
                    <ItemQty>{item.qty}</ItemQty>
                    <QtyBtn
                      onClick={() => addToCart({ ...item, qty: 1 })}
                      title="Increase"
                    >
                      +
                    </QtyBtn>
                  </QtyControls>
                  <RemoveBtn
                    onClick={() => removeFromCart(item.id)}
                    title="Remove"
                  >
                    {FaTrash({}) as React.ReactElement}
                  </RemoveBtn>
                </CartItemActions>
              </CartItem>
            ))}
          </CartList>
          <Total>Total: ${total.toFixed(2)}</Total>
          <CheckoutBtn onClick={handleCheckout}>Checkout</CheckoutBtn>
        </>
      )}
    </Wrapper>
  );
};

export default Cart;
